/**
 * The message protocol between the main thread and the database worker. Types only,
 * shared by both sides (this file is compiled under the DOM and the webworker libs, so
 * it names no platform globals).
 *
 * Requests travel as `{ id, type, payload }` and are answered by `{ id, ok, result }` or
 * `{ id, ok: false, error }`. The worker also emits events without an id (`db:ready`,
 * `sync:state`, `snapshot:progress`, `sync:session`) and asks the main thread for a
 * bearer token with `auth:token` (spec 9.7 step 1), answered by `{ type: 'auth:token',
 * id, token }`.
 */
import type { LocalWrite, Op, SyncErrorCode, SyncScope } from '@satu/shared'
import type { FtsTable } from './fts'

export type Storage = 'opfs' | 'memory'

/**
 * Why the database is in memory: the browser has no OPFS SyncAccessHandle (show the
 * banner "Penyimpanan tetap tidak tersedia"), or another context still held the pool's
 * handles when this worker started (the client respawns it).
 */
export type StorageReason = 'unsupported' | 'busy'

export interface DbReadyInfo {
  /** SQLite library version. */
  version: string
  /** SCHEMA_VERSION the device database now matches (also `PRAGMA user_version`). */
  schemaVersion: number
  /** Where the database lives; 'memory' only when the OPFS pool is unavailable. */
  storage: Storage
  storageReason: StorageReason | null
  /** Migrations applied by this start; 0 on a warm launch. */
  migrationsApplied: number
  /** Worker start to database ready, in ms (also `performance.mark('simu:db-ready')`). */
  bootMs: number
}

export type SyncPhase = 'idle' | 'token' | 'push' | 'pull' | 'snapshot'

/** Broadcast on every phase change (spec 9.7: shown in the UI at all times). */
export interface SyncState {
  lastSyncAt: string | null
  connected: boolean
  syncing: boolean
  /** Ops waiting in the outbox (retired document uploads have no count, spec 2026-09-30, 9). */
  pending: number
  /** Open rows in masalah_sinkron. */
  problems: number
  /** Local clock minus server clock at the last exchange, in ms. */
  driftMs: number
  phase: SyncPhase
  lastError: string | null
}

export const INITIAL_SYNC_STATE: SyncState = {
  lastSyncAt: null,
  connected: false,
  syncing: false,
  pending: 0,
  problems: 0,
  driftMs: 0,
  phase: 'idle',
  lastError: null,
}

/** First-install progress, one event per snapshot page (DISCUSSION-sinkron). */
export interface SnapshotProgress {
  phase: 'mulai' | 'tabel' | 'selesai' | 'gagal'
  table?: string
  received?: number
  /** From `counts` of /sync/snapshot/begin when the server sends them. */
  total?: number | null
  counts?: Record<string, number>
}

/** A request-level error that ends the session or asks for an update (spec 15.2, 9.10). */
export interface SyncSessionEvent {
  reason: SyncErrorCode
  message?: string
}

export interface QueryRequest {
  sql: string
  params: unknown[]
  method: 'run' | 'all' | 'values' | 'get'
}

/** Rows as arrays of values, the shape drizzle-orm/sqlite-proxy expects. */
export interface QueryResult {
  rows: unknown[] | undefined
}

export interface SearchRequest {
  query: string
  tables?: FtsTable[]
  /** Per table; default 10. */
  limit?: number
}

export interface SearchHit {
  table: FtsTable
  id: string
  label: string
  detail: string | null
  /** bm25 rank, lower is better; trigram hits come after prefix hits. */
  rank: number
}

export interface MasalahRow {
  id: string
  op_id: string | null
  tabel: string | null
  row_id: string | null
  jenis: string
  pesan: string | null
  baris_server: string | null
  /** The rejected op as JSON text (section 16), null when the problem has no op. */
  op: string | null
  dibuat_pada: string
  ditutup_pada: string | null
}

export interface BackupExport {
  /** The whole database file (sqlite3_serialize). */
  database: Uint8Array
  /** Pending ops, so a restore can push what the file already contains. */
  outbox: Op[]
  exportedAt: string
}

export type Empty = Record<string, never>

/**
 * Release 20 (Peta umat): write only while one row still holds these values, judged inside the
 * write's own transaction. The background address lookup decides on a family as it read it; a
 * person's change in between (a GPS pin) makes the lookup's write a no-op, never an overwrite.
 */
export interface JikaMasih {
  table: string
  row_id: string
  kolom: Record<string, unknown>
}

/** Every request the worker answers, with its payload and result. */
export interface Requests {
  'db:query': { payload: QueryRequest; result: QueryResult }
  'db:batch': { payload: { queries: QueryRequest[] }; result: QueryResult[] }
  /**
   * Save: apply local writes, stamp ops and append them to the outbox in one transaction. Plan
   * 10: `paroki`, the parish the screen worked in when it read what it saves (a letter's number,
   * a book's place); when it is not the worker's parish of work now (a switch landed in between)
   * nothing is written and the request fails (`tempat_kerja_berganti`). Absent: not checked.
   * Release 20: `jikaMasih`, a row's values the writes depend on; when the row holds others now,
   * nothing is written and the answer has no op. Absent: not checked.
   */
  'ops:apply': {
    payload: { writes: LocalWrite[]; paroki?: string | null; jikaMasih?: JikaMasih }
    result: { ops: Op[] }
  }
  /**
   * The acting user and device after login (section 05); null on logout. Plan 10: `kerja`, the
   * parish an admin keuskupan works in (the person's own stored choice); absent or null: none.
   */
  'sesi:set': { payload: { scope: SyncScope | null; kerja?: string | null }; result: SyncState }
  /** Plan 10: work in another parish (null: the diocese view); answers the one that applies. */
  'sesi:kerja': { payload: { kerja: string | null }; result: { kerja: string | null } }
  'sync:now': { payload: Empty; result: SyncState }
  /**
   * Release 17: the browser's online event (`daring`) or a return to the app (`kembali`) asks
   * for a run now, the back-off reset; answers at once with the state (the run reports itself).
   */
  'sync:segera': { payload: { pemicu: 'daring' | 'kembali' }; result: SyncState }
  /**
   * Release 17 review M-4: a guided flow is on screen (`terbuka`) or no longer: while it is, a
   * save waits longer before it is sent (../sync/loop `SAVE_DEBOUNCE_ALUR_MS`).
   */
  'sync:alur': { payload: { terbuka: boolean }; result: SyncState }
  'sync:state': { payload: Empty; result: SyncState }
  search: { payload: SearchRequest; result: SearchHit[] }
  'masalah:list': { payload: { termasukDitutup?: boolean }; result: MasalahRow[] }
  /** Close one problem, or every open one when id is null. */
  'masalah:tutup': { payload: { id: string | null }; result: { ditutup: number } }
  'backup:export': { payload: Empty; result: BackupExport }
  /** Tab hand-over (lock.ts): close the database and release the OPFS handles. */
  'db:pause': { payload: Empty; result: { paused: boolean } }
  'db:resume': { payload: Empty; result: DbReadyInfo }
}

export type RequestType = keyof Requests

/**
 * Namespaces reserved for later sections; the worker answers `not_implemented` until a
 * handler is registered in `WorkerContext.extensions`.
 */
export type ReservedRequestType =
  | 'aggregates:get'
  | `register:${string}`
  | `keluarga:${string}`
  | `umat:${string}`
  | `masalah:${string}`
  | `baca:${string}`
  | `sinkron:${string}`
  | `impor:${string}`
  | `mutasi:${string}`
  | `pendaftaran:${string}`
  | `surat:${string}`
  | `laporan:${string}`
  | `pengaturan:${string}`
  | `admin:${string}`
  | `pelayan:${string}`
  | `peta:${string}`

export interface RequestEnvelope {
  id: number
  type: RequestType | ReservedRequestType
  payload: unknown
}

export interface WorkerError {
  code: string
  message: string
}

export type ResponseEnvelope =
  | { id: number; ok: true; result: unknown }
  | { id: number; ok: false; error: WorkerError }

export type WorkerEvent =
  | { type: 'db:ready'; info: DbReadyInfo }
  | { type: 'db:error'; error: WorkerError }
  | { type: 'sync:state'; state: SyncState }
  | { type: 'snapshot:progress'; progress: SnapshotProgress }
  | { type: 'sync:session'; session: SyncSessionEvent }
  | { type: 'auth:token'; id: number }

export interface TokenResponseMessage {
  type: 'auth:token'
  id: number
  token: string | null
}

export type MainToWorker = RequestEnvelope | TokenResponseMessage
export type WorkerToMain = ResponseEnvelope | WorkerEvent

export function isTokenResponse(message: MainToWorker): message is TokenResponseMessage {
  return message.type === 'auth:token' && 'token' in message
}

export function isResponse(message: WorkerToMain): message is ResponseEnvelope {
  return 'ok' in message && typeof message.id === 'number'
}
